(() => {
  'use strict';
  // One internal unit is 0.000001 cm. Eighth inches convert exactly (1/8 in = 0.3175 cm).
  const CM_SCALE = 1000000;
  const INCH_UNITS = 2540000;
  const FRACTIONS = ['0', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8'];
  const formatCm = (units) => (units / CM_SCALE).toFixed(6).replace(/\.?0+$/, '');
  const displayCm = (units) => (units / CM_SCALE).toFixed(1);
  const parseCm = (value) => {
    const text = String(value).trim();
    if (!/^(?:\d+(?:\.\d{0,6})?|\.\d{1,6})$/.test(text)) throw new Error('Enter a measurement in cm with up to 6 decimal places.');
    const units = Math.round(Number(text) * CM_SCALE);
    if (!Number.isSafeInteger(units) || units <= 0) throw new Error('Enter a measurement greater than zero.');
    return units;
  };
  const gcd = (a, b) => { while (b) [a, b] = [b, a % b]; return a; };
  const inchParts = (units) => {
    const whole = Math.floor(units / INCH_UNITS), remainder = units % INCH_UNITS;
    const divisor = remainder ? gcd(remainder, INCH_UNITS) : 1;
    return { whole, remainder, fraction: remainder ? `${remainder / divisor}/${INCH_UNITS / divisor}` : '0' };
  };
  const formatInches = (units) => {
    const part = inchParts(units);
    if (!FRACTIONS.includes(part.fraction)) return `≈${Number((units / INCH_UNITS).toFixed(6))}`;
    return `${part.whole}${part.remainder ? ` ${part.fraction}` : ''}`;
  };
  const fromInches = (whole, fraction = '0') => {
    if (!/^\d+$/.test(String(whole).trim())) throw new Error('Enter whole inches and choose a fraction.');
    let fractionalUnits = 0;
    if (fraction !== '0') {
      const match = /^(\d+)\/(\d+)$/.exec(String(fraction));
      if (!match || Number(match[2]) <= 0 || Number(match[1]) >= Number(match[2])) throw new Error('Choose a valid inch fraction.');
      fractionalUnits = Number(match[1]) * INCH_UNITS / Number(match[2]);
      if (!Number.isSafeInteger(fractionalUnits)) throw new Error('This fraction cannot be converted exactly. Please enter the measurement in cm.');
    }
    const units = Number(whole) * INCH_UNITS + fractionalUnits;
    if (!Number.isSafeInteger(units) || units <= 0) throw new Error('Enter a measurement greater than zero.');
    return units;
  };
  const measurementState = (state) => {
    const widthUnits = state.widthCm !== undefined ? parseCm(state.widthCm) : Math.round(state.width * INCH_UNITS);
    const lengthUnits = state.lengthCm !== undefined ? parseCm(state.lengthCm) : Math.round(state.length * INCH_UNITS);
    return { width: widthUnits / INCH_UNITS, length: lengthUnits / INCH_UNITS, widthCm: formatCm(widthUnits), lengthCm: formatCm(lengthUnits), widthUnits, lengthUnits, measurementUnit: state.measurementUnit === 'cm' ? 'cm' : 'in' };
  };
  const hasExactConversion = (quote) => quote.measurementUnit === 'in' && ['width', 'length'].some((dimension) => !FRACTIONS.includes(inchParts(quote[`${dimension}Units`]).fraction));
  const dimensionLabel = (quote, dimension) => {
    if (quote.measurementUnit === 'cm') return `${formatCm(quote[`${dimension}Units`])} cm`;
    const units = quote[`${dimension}Units`];
    return FRACTIONS.includes(inchParts(units).fraction) ? `${formatInches(units)} in` : `${formatCm(units)} cm`;
  };
  const sizeLabel = (quote) => hasExactConversion(quote) ? `${formatCm(quote.widthUnits)} cm W × ${formatCm(quote.lengthUnits)} cm L` : `${dimensionLabel(quote, 'width')} W × ${dimensionLabel(quote, 'length')} L`;
  const productionProperties = (quote) => ({
    'Finished panel width': dimensionLabel(quote, 'width'),
    'Finished panel length': dimensionLabel(quote, 'length'),
    'Customer measurement unit': quote.measurementUnit,
    'Factory width (cm)': formatCm(quote.widthUnits),
    'Factory length (cm)': formatCm(quote.lengthUnits),
    'Factory unit': 'cm',
    'Measurement basis': 'Finished single panel; pleated headings use width after pleating',
    'Factory deduction': 'None; finished dimensions, do not deduct',
    '_Kalia measurement schema': 'finished-panel-cm-v1',
    '_Kalia exact width (cm)': quote.widthCm,
    '_Kalia exact length (cm)': quote.lengthCm,
    '_Kalia pricing width (in)': String(quote.pricingWidth),
    '_Kalia pricing length (in)': String(quote.pricingLength)
  });
  window.KaliaDraperyMeasurements = { parseCm, formatCm, displayCm, fromInches, inchParts, formatInches, measurementState, productionProperties };
  const edgePrice = (tiers, type, value) => {
    const tier = tiers.find((entry) => entry.edgeType === type && value >= entry.min && value <= entry.max);
    if (!tier) throw new Error('This measurement is not available. Choose a size within the listed range.');
    if (tier.type === 'exact') return Math.round(tier.price * 100);
    if (Number.isFinite(tier.stepStart) && tier.stepStart >= 0) return Math.round(tier.stepStart * 100) + Math.ceil((value - tier.min) / tier.stepSize) * Math.round(tier.stepPrice * 100);
    return Math.round(tier.price * 100);
  };
  const calculate = (config, state) => {
    const measurements = measurementState(state);
    const { width, length } = measurements;
    const { quantity } = state;
    const rules = config.size.validation;
    if (!Number.isFinite(width) || width < rules.minWidth || width > rules.maxWidth) throw new Error(`Enter a finished panel width from ${rules.minWidth} to ${rules.maxWidth} inches (${formatCm(rules.minWidth * INCH_UNITS)}–${formatCm(rules.maxWidth * INCH_UNITS)} cm).`);
    if (!Number.isFinite(length) || length < rules.minLength || length > rules.maxLength) throw new Error(`Enter a finished panel length from ${rules.minLength} to ${rules.maxLength} inches (${formatCm(rules.minLength * INCH_UNITS)}–${formatCm(rules.maxLength * INCH_UNITS)} cm).`);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10) throw new Error('Choose from 1 to 10 individual panels.');
    const header = config.headers[state.header];
    const lining = config.linings[state.lining];
    const color = config.colors[state.color];
    if (!header || !lining || !color) throw new Error('Choose a fabric color, heading and lining.');
    // Keep the existing whole-inch price catalog separate from the exact production dimensions.
    const pricingWidth = Math.ceil(width - 1e-9), pricingLength = Math.ceil(length - 1e-9);
    const unitCents = config.sourceBaseCents + header.cents + lining.cents + edgePrice(config.size.tiers, 'width', pricingWidth) + edgePrice(config.size.tiers, 'length', pricingLength);
    return { unitCents, totalCents: unitCents * quantity, header, lining, color, ...measurements, pricingWidth, pricingLength, quantity };
  };
  const priceLabel = (cents) => `USD ${(cents / 100).toFixed(2)}`;
  window.KaliaDraperyPricing = { edgePrice, calculate, priceLabel };

  class KaliaDrapery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.version = 0;
      this.products = new Map();
      this.form = this.querySelector('[data-drapery-form]');
      this.button = this.querySelector('[data-add]');
      this.message = this.querySelector('[data-message]');
      this.confirm = this.querySelector('[data-purchase-confirm]');
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.add(); });
      const changed = (event) => {
        if (this.adding || event.target.closest('[data-drapery-hardware]')) return;
        if (event.target !== this.confirm) this.confirm.checked = false;
        this.update();
      };
      this.form.addEventListener('change', changed);
      this.form.addEventListener('input', (event) => { if (event.target.type === 'number') changed(event); });
      this.setupColorGallery();
      this.initialize();
      this.onPageShow = (event) => { if (event.persisted) window.location.reload(); };
      window.addEventListener('pageshow', this.onPageShow);
    }
    disconnectedCallback() { if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow); this.hardware?.destroy(); this.version++; }
    async json(url, fresh = false) {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: fresh ? 'no-store' : 'default' });
      if (!response.ok) throw new Error('We could not load the current product options. Please try again.');
      return response.json();
    }
    async initialize() {
      try {
        const [configs, prices] = await Promise.all([this.json(this.dataset.configUrl), this.json(this.dataset.priceIndexUrl)]);
        this.config = configs[this.dataset.handle];
        this.priceIndex = prices;
        if (!this.config) throw new Error('This fabric is currently unavailable.');
        if ((this.dataset.currency || 'USD') !== 'USD') throw new Error('Please choose USD as your store currency to configure this collection.');
        this.buildFields();
        this.hardware = new window.KaliaDraperyHardware(this.querySelector('[data-drapery-hardware]'), {
          rootUrl: this.dataset.rootUrl,
          configUrl: this.dataset.hardwareConfigUrl,
          customConfigUrl: this.dataset.hardwareCustomUrl,
          priceIndexUrl: this.dataset.hardwarePriceUrl,
          pricingUrl: this.dataset.hardwarePricingUrl,
          money: (cents) => this.money(cents),
          onChange: (state) => {
            this.hardwareState = state;
            if (this.adding) return;
            this.confirm.checked = false;
            this.update();
          }
        });
        // A temporarily unavailable optional catalog must not block fabric-only orders.
        try { await this.hardware.initialize(); } catch (_) { this.hardwareState = {selected: false, ready: true, totalCents: 0}; }
        if (window.KaliaDraperyDesign) { this.design = new window.KaliaDraperyDesign(this); await this.design.initialize(); }
        else if (new URLSearchParams(window.location.search || '').has('kalia_edit') || new URLSearchParams(window.location.search || '').has('kalia_copy')) throw new Error('The saved design tools could not load. Return to your bag and reopen this window.');
        this.galleryRestoring = false;
        this.querySelector('[data-loading]').hidden = true;
        this.querySelector('[data-summary]').hidden = false;
        await this.update();
      } catch (error) { this.setMessage(error.message, true); this.querySelector('[data-loading]').hidden = true; }
    }
    node(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    static colorPhotoURL(value, existingSample = false) {
      if (typeof value !== 'string' || !value || value !== value.trim() || value.includes('\\')) return '';
      try {
        const url = new URL(value, document.baseURI);
        if (url.protocol !== 'https:' || url.username || url.password || url.port || url.hash) return '';
        if (url.hostname === 'imagedelivery.net' && !url.search && /^\/nERRffKFW813-Rl-Ln6q8Q\/[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\/(?:large|thumbnail)$/.test(url.pathname)) {
          url.pathname = url.pathname.replace(/\/thumbnail$/, '/large'); return url.href;
        }
        // Native URLs are only reused from the existing selected sample or SSR gallery.
        if (existingSample && (url.hostname === 'cdn.shopify.com' && /^\/s\/files\/1\/0728\/7422\/9857\/(?:files|products)\/[^/]+\.(?:jpe?g|png|webp|avif)$/i.test(url.pathname) || ['kaliashades.com', 'www.kaliashades.com', 'sp20uq-sf.myshopify.com'].includes(url.hostname) && /^\/cdn\/shop\/(?:files|products)\/[^/]+\.(?:jpe?g|png|webp|avif)$/i.test(url.pathname))) return url.href;
      } catch (_) { /* An unverified photo never enters the color gallery. */ }
      return '';
    }
    colorPhotos(color) {
      if (!color) return [];
      const valid = this.constructor.colorPhotoURL;
      const sample = valid(color.preview, true) || valid(color.image, true);
      const seen = new Set(), photos = [];
      const add = (url, role) => {
        if (!url || seen.has(url)) return;
        seen.add(url); photos.push({ url, role, key: JSON.stringify([this.dataset.handle, color.label, role, url]) });
      };
      const entries = (Array.isArray(color.gallery) ? color.gallery : []).filter(photo => photo && ['color-reference', 'detail', 'fabric'].includes(photo.role));
      for (const role of ['color-reference', 'detail']) entries.filter(photo => photo.role === role).forEach(photo => add(valid(photo.url), role));
      // The original exact swatch remains the authoritative fabric close-up.
      entries.filter(photo => photo.role === 'fabric').forEach(photo => { const url = valid(photo.url, true); if (url === sample) add(url, 'fabric'); });
      add(sample, 'fabric');
      return photos;
    }
    setupColorGallery() {
      if (this.colorGalleryReady) return true;
      const gallery = this.querySelector('.kd-gallery'), stage = this.querySelector('.kd-main-image'), thumbs = this.querySelector('.kd-thumbnails');
      if (!gallery || !stage || !thumbs) return false;
      this.galleryStage = stage; this.galleryThumbs = thumbs;
      this.generalPhotos = [...thumbs.querySelectorAll('[data-gallery-image]')].flatMap((button, index) => {
        const url = this.constructor.colorPhotoURL(button.dataset.galleryImage, true);
        const thumbnail = this.constructor.colorPhotoURL(button.querySelector('img')?.getAttribute('src'), true);
        return url ? [{ url, thumbnail: thumbnail || url, role: 'general', key: JSON.stringify(['general', this.dataset.handle, index, url]) }] : [];
      });
      const hero = stage.querySelector('.kd-hero-image');
      if (!this.generalPhotos.length && hero) {
        const url = this.constructor.colorPhotoURL(hero.getAttribute('src'), true);
        if (url) this.generalPhotos.push({ url, role: 'general', key: JSON.stringify(['general', this.dataset.handle, 0, url]) });
      }
      const modes = this.node('div', 'kd-gallery-modes'); modes.setAttribute('role', 'group'); modes.setAttribute('aria-label', 'Product image groups');
      this.galleryModes = ['color', 'details'].map(mode => {
        const button = this.node('button', 'kd-gallery-mode', mode === 'color' ? 'Selected fabric' : 'Product details');
        button.type = 'button'; button.dataset.draperyGalleryMode = mode; button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this.showGalleryMode(mode)); modes.append(button); return button;
      });
      gallery.insertBefore(modes, stage);
      this.galleryCaption = this.node('p', 'kd-photo-caption'); this.galleryCaption.setAttribute('aria-live', 'polite');
      this.galleryNote = this.node('span', 'kd-photo-scope');
      this.galleryCaption.append(this.node('strong'), this.galleryNote); stage.after(this.galleryCaption);
      thumbs.addEventListener('click', event => {
        const button = event.target.closest('[data-gallery-key]');
        if (!button || !thumbs.contains(button)) return;
        const photo = this.activeGalleryPhotos?.find(entry => entry.key === button.dataset.galleryKey);
        if (photo) this.showGalleryPhoto(photo);
      });
      this.colorGalleryReady = true; this.galleryRestoring = true; return true;
    }
    syncColorGallery() {
      if (!this.colorGalleryReady || this.galleryRestoring || !this.config) return;
      const input = this.form?.elements?.namedItem('color'), value = input?.value;
      const index = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : -1;
      const color = Number.isSafeInteger(index) && index >= 0 ? this.config.colors?.[index] : null;
      const key = JSON.stringify([this.dataset.handle, color?.label || null]);
      if (key === this.galleryColorKey) return;
      this.galleryColorKey = key; this.galleryColorLabel = color?.label || '';
      this.selectedColorPhotos = this.colorPhotos(color); this.galleryFailures = new Set();
      this.colorPhotoKey = this.selectedColorPhotos[0]?.key || '';
      this.showGalleryMode(this.selectedColorPhotos.length ? 'color' : 'details');
    }
    showGalleryMode(mode) {
      if (!this.colorGalleryReady) return;
      const photos = mode === 'details' ? this.generalPhotos : this.selectedColorPhotos || [];
      this.galleryMode = mode; this.activeGalleryPhotos = photos;
      this.galleryModes.forEach(button => {
        const name = button.dataset.draperyGalleryMode;
        button.disabled = !(name === 'color' ? this.selectedColorPhotos?.length : this.generalPhotos.length);
        button.setAttribute('aria-pressed', String(name === mode));
      });
      if (!photos?.length) {
        this.galleryThumbs.replaceChildren(); this.gallerySignature = ''; this.galleryDisplayedKey = '';
        if (this.galleryHero) this.galleryHero.hidden = true;
        this.galleryCaption.querySelector('strong').textContent = this.galleryColorLabel ? `Images for ${this.galleryColorLabel} are unavailable` : 'Choose a fabric color';
        this.galleryNote.textContent = ''; return;
      }
      const signature = JSON.stringify([mode, photos.map(photo => photo.key)]);
      if (signature !== this.gallerySignature) {
        this.gallerySignature = signature; this.galleryThumbs.replaceChildren();
        this.galleryThumbs.setAttribute('aria-label', mode === 'details' ? 'General product photographs' : `Images for ${this.galleryColorLabel}`);
        photos.forEach((photo, index) => {
          const button = this.node('button', 'kd-thumb'); button.type = 'button';
          button.dataset.galleryImage = photo.url; button.dataset.galleryKey = photo.key;
          button.setAttribute('aria-label', `View ${this.galleryPhotoLabel(photo)} ${index + 1}`); button.setAttribute('aria-pressed', 'false');
          const image = this.node('img'); image.src = photo.thumbnail || photo.url; image.dataset.zoomSrc = photo.url;
          image.alt = this.galleryPhotoLabel(photo); image.loading = 'lazy'; image.width = 120; image.height = 120;
          button.append(image); this.galleryThumbs.append(button);
        });
      }
      const saved = mode === 'details' ? this.detailPhotoKey : this.colorPhotoKey;
      this.showGalleryPhoto(photos.find(photo => photo.key === saved && !this.galleryFailures.has(photo.key)) || photos.find(photo => !this.galleryFailures.has(photo.key)) || photos[0]);
    }
    galleryPhotoLabel(photo) {
      if (photo.role === 'general') return 'Product details · Other colors may be shown';
      return `${photo.role === 'fabric' ? 'Fabric close-up' : photo.role === 'detail' ? 'Fabric detail' : 'Color reference'} · ${this.galleryColorLabel}`;
    }
    showGalleryPhoto(photo) {
      if (!photo || !this.activeGalleryPhotos?.some(entry => entry.key === photo.key)) return;
      if (this.galleryMode === 'details') this.detailPhotoKey = photo.key; else this.colorPhotoKey = photo.key;
      this.galleryThumbs.querySelectorAll('[data-gallery-key]').forEach(button => {
        const active = button.dataset.galleryKey === photo.key;
        button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active));
      });
      this.galleryCaption.querySelector('strong').textContent = this.galleryPhotoLabel(photo);
      this.galleryNote.textContent = photo.role === 'fabric' ? 'Compare color and texture with your physical swatch.' : photo.role === 'general' ? 'General product photography.' : 'Heading, lining and hardware may differ.';
      if (this.galleryDisplayedKey === photo.key && this.galleryHero?.isConnected) {
        if (this.galleryFailures.has(photo.key)) this.galleryCaption.querySelector('strong').textContent = `${this.galleryPhotoLabel(photo)} · Image unavailable`;
        return;
      }
      this.galleryDisplayedKey = photo.key;
      const image = this.node('img', 'kd-hero-image'); image.alt = this.galleryPhotoLabel(photo); image.decoding = 'async';
      image.width = 1600; image.height = 1600; image.dataset.galleryKey = photo.key; image.dataset.zoomSrc = photo.url;
      const colorKey = this.galleryColorKey;
      image.addEventListener('error', () => {
        const failedURL = image.getAttribute('src');
        queueMicrotask(() => {
          if (this.galleryHero !== image || !image.isConnected || colorKey !== this.galleryColorKey || image.getAttribute('src') !== failedURL) return;
          // A brand-image fallback can restore its original URL before this microtask.
          this.galleryFailures.add(photo.key);
          const fallback = this.activeGalleryPhotos.find(entry => !this.galleryFailures.has(entry.key));
          if (fallback) this.showGalleryPhoto(fallback);
          else { image.hidden = true; this.galleryCaption.querySelector('strong').textContent = `${this.galleryPhotoLabel(photo)} · Image unavailable`; }
        });
      });
      this.galleryHero = image; this.galleryStage.replaceChildren(image); image.src = photo.url;
    }
    fieldset(number, label, key) {
      const fieldset = this.node('fieldset', 'kd-fieldset');
      fieldset.id = `kd-field-${key}`;
      const legend = this.node('legend');
      legend.append(this.node('span', 'kd-step', number), this.node('span', '', label));
      const selected = this.node('span', 'kd-selected-value');
      selected.dataset.selected = key;
      legend.append(selected);
      const content = this.node('div', 'kd-field-content');
      fieldset.append(legend, content);
      this.querySelector('[data-fields]').append(fieldset);
      return content;
    }
    choices(container, name, options, defaultIndex, kind) {
      const list = this.node('div', kind === 'color' ? 'kd-swatch-list' : `kd-choice-list${kind === 'lining' ? ' kd-linings' : ''}`);
      options.forEach((option, index) => {
        const label = this.node('label', `kd-choice ${kind === 'color' ? 'kd-swatch' : 'kd-option-card'}`);
        label.title = option.label;
        const input = document.createElement('input');
        input.type = 'radio'; input.name = name; input.value = index; input.required = true; input.checked = index === defaultIndex;
        input.setAttribute('aria-label', option.label);
        label.append(input);
        if (option.image) { const image = document.createElement('img'); image.src = option.image; image.alt = ''; image.loading = 'lazy'; label.append(image); }
        if (kind !== 'color') {
          const description = window.KaliaDraperyDesign?.describe(kind, option);
          const copy = this.node('span', 'kd-option-copy');
          copy.append(this.node('span', 'kd-option-label', description?.title || option.label));
          if (description?.help) copy.append(this.node('span', 'kd-option-help', description.help));
          if (description?.title !== option.label) copy.append(this.node('span', 'kd-option-technical', option.label));
          if (Number.isSafeInteger(option.cents) && Number.isSafeInteger(options[defaultIndex]?.cents)) {
            const difference = option.cents - options[defaultIndex].cents;
            copy.append(this.node('span', 'kd-option-price', difference ? `${difference > 0 ? '+' : '−'}${this.money(Math.abs(difference))} / panel vs standard` : 'Standard price'));
          }
          label.append(copy);
        }
        list.append(label);
      });
      container.append(list);
    }
    numberInput(container, name, label, minimum, maximum, value, suffix) {
      const wrapper = this.node('label', 'kd-label', label);
      const wrap = this.node('div', 'kd-input-wrap');
      const input = document.createElement('input');
      Object.assign(input, { type: 'number', name, min: minimum, max: maximum, step: 1, value, required: true, className: 'kd-input' });
      input.inputMode = 'numeric';
      wrap.append(input);
      if (suffix) wrap.append(this.node('span', '', suffix));
      wrapper.append(wrap);
      if (name !== 'quantity') wrapper.append(this.node('p', 'kd-range-note', `${minimum}–${maximum} inches`));
      container.append(wrapper);
    }
    buildFields() {
      const config = this.config;
      const colorIndex = Math.max(0, config.colors.findIndex((color) => color.label === config.defaultColor));
      this.choices(this.fieldset('1', 'Fabric color', 'color'), 'color', config.colors, colorIndex, 'color');
      this.choices(this.fieldset('2', 'Hanging heading', 'header'), 'header', config.headers, 0, 'header');
      const size = this.fieldset('3', 'Finished panel size', 'size');
      const measuringTools = this.querySelector('[data-drapery-measuring-tools]');
      if (measuringTools) size.append(measuringTools.content.cloneNode(true));
      this.buildMeasurements(size);
      if (!measuringTools) {
        const guide = this.node('a', 'kd-measure-link', 'Check the measuring guide ↗'); guide.href = `${this.dataset.rootUrl || '/'}pages/how-to-measure#drapery-measuring`;
        const dialog = this.querySelector('dialog[data-kalia-config-guide][id$="-measure-drapery"]');
        if (dialog) { guide.classList.add('kcg-trigger'); guide.dataset.kaliaGuideOpen = dialog.id; guide.setAttribute('aria-haspopup', 'dialog'); guide.setAttribute('aria-controls', dialog.id); }
        size.append(guide);
      }
      this.choices(this.fieldset('4', 'Lining', 'lining'), 'lining', config.linings, 0, 'lining');
      const quantity = this.fieldset('5', 'Panel quantity', 'quantity');
      const quantityLine = this.node('div', 'kd-quantity-line');
      this.numberInput(quantityLine, 'quantity', 'Panels', 1, 10, 2);
      quantityLine.append(this.node('p', 'kd-range-note', 'One panel per quantity. Select 2 for a pair.'));
      const room = this.node('label', 'kd-label kd-room', 'Room or window name (optional)');
      const input = document.createElement('input'); input.name = 'room'; input.className = 'kd-input'; input.maxLength = 80; input.placeholder = 'For example: Living room, left window'; room.append(input);
      quantity.append(quantityLine, room);
    }
    buildMeasurements(size) {
      this.measurementUnit = 'in';
      this.measurementFields = {};
      const controls = this.node('div', 'kd-unit-controls');
      controls.append(this.node('span', 'kd-unit-label', 'Measurements for one finished panel'));
      const toggle = this.node('div', 'kd-unit-toggle');
      toggle.setAttribute('role', 'group'); toggle.setAttribute('aria-label', 'Measurement unit');
      this.unitButtons = ['in', 'cm'].map((unit) => {
        const button = this.node('button', 'kd-unit-button', unit === 'in' ? 'inches' : 'cm');
        button.type = 'button'; button.dataset.unit = unit; button.setAttribute('aria-pressed', String(unit === 'in'));
        button.addEventListener('click', () => this.setMeasurementUnit(unit));
        toggle.append(button); return button;
      });
      controls.append(toggle);
      const measurements = this.node('div', 'kd-measures');
      const rules = this.config.size.validation;
      this.measurementInput(measurements, 'width', 'Panel width', rules.minWidth, rules.maxWidth, Math.max(rules.minWidth, Math.min(50, rules.maxWidth)));
      this.measurementInput(measurements, 'length', 'Panel length', rules.minLength, rules.maxLength, Math.max(rules.minLength, Math.min(84, rules.maxLength)));
      size.append(controls, measurements, this.node('p', 'kd-measure-help', 'Enter finished dimensions for one panel. For pleated headings, use the width after pleating; do not add fabric fullness or deduct. Changing units preserves your exact size.'));
    }
    measurementInput(container, name, label, minimum, maximum, value) {
      const wrapper = this.node('div', 'kd-label kd-measurement-field');
      const id = `${this.dataset.handle || 'drapery'}-${name}`;
      const title = this.node('label', '', label); title.htmlFor = id;
      const row = this.node('div', 'kd-measurement-inputs');
      const wrap = this.node('div', 'kd-input-wrap');
      const input = this.node('input', 'kd-input');
      Object.assign(input, { id, type: 'number', name, min: minimum, max: maximum, step: 1, value: String(value), required: true, inputMode: 'numeric' });
      const suffix = this.node('span', '', 'in'); wrap.append(input, suffix);
      const fractionWrap = this.node('label', 'kd-fraction-label', 'Fraction');
      const fraction = this.node('select', 'kd-input kd-fraction'); fraction.name = `${name}_fraction`; fraction.setAttribute('aria-label', `${label} fraction`);
      FRACTIONS.forEach((part) => { const option = this.node('option', '', part); option.value = part; fraction.append(option); });
      fraction.value = '0'; fractionWrap.append(fraction); row.append(wrap, fractionWrap);
      const range = this.node('p', 'kd-range-note', `${minimum}–${maximum} inches`);
      const converted = this.node('p', 'kd-measurement-cm', `Finished size: ${formatCm(value * INCH_UNITS)} cm`); converted.setAttribute('aria-live', 'polite');
      wrapper.append(title, row, range, converted); container.append(wrapper);
      this.measurementFields[name] = { input, suffix, fraction, fractionWrap, range, converted, minimum, maximum, units: value * INCH_UNITS, exactOption: null, displayUnit: 'in', displayValue: String(value), displayFraction: '0', displayEdited: false };
      input.addEventListener('input', () => { this.measurementFields[name].displayEdited = true; });
      input.addEventListener('change', () => this.finishMeasurementInput(name));
      input.addEventListener('blur', () => this.finishMeasurementInput(name));
      input.addEventListener('keydown', (event) => { if (event.key === 'Enter') this.finishMeasurementInput(name); });
    }
    readMeasurementField(field) {
      if (this.measurementUnit === 'in' && !FRACTIONS.includes(field.fraction.value) && field.fraction.value !== field.exactOption?.value) throw new Error('Choose an available inch fraction.');
      // A rendered cm value is presentation only. Preserve the canonical value until an actual edit.
      const unchanged = !field.displayEdited && field.displayUnit === this.measurementUnit && field.displayValue === field.input.value && (this.measurementUnit === 'cm' || field.displayFraction === field.fraction.value);
      const units = unchanged ? field.units : this.measurementUnit === 'cm' ? parseCm(field.input.value) : fromInches(field.input.value, field.fraction.value);
      field.units = units;
      field.displayUnit = this.measurementUnit; field.displayValue = field.input.value; field.displayFraction = field.fraction.value; field.displayEdited = false;
      field.converted.textContent = `Finished size: ${formatCm(units)} cm`;
      return units;
    }
    readMeasurementFields() {
      const result = {};
      Object.entries(this.measurementFields).forEach(([name, field]) => {
        const units = this.readMeasurementField(field);
        result[name] = units / INCH_UNITS; result[`${name}Cm`] = formatCm(units);
      });
      return { ...result, measurementUnit: this.measurementUnit };
    }
    renderMeasurementField(field) {
      const unit = this.measurementUnit;
      if (field.exactOption) { field.exactOption.remove(); field.exactOption = null; }
      field.suffix.textContent = unit;
      field.input.inputMode = unit === 'in' ? 'numeric' : 'decimal'; field.input.step = unit === 'in' ? '1' : '0.000001';
      // Exact cm fields share the native micro-cm precision and exact range anchors.
      field.input.min = unit === 'in' ? field.minimum : formatCm(field.minimum * INCH_UNITS);
      field.input.max = unit === 'in' ? field.maximum : formatCm(field.maximum * INCH_UNITS);
      field.fractionWrap.hidden = unit !== 'in'; field.fraction.disabled = unit !== 'in';
      if (unit === 'cm') field.input.value = formatCm(field.units);
      else {
        const parts = inchParts(field.units); field.input.value = String(parts.whole);
        if (!FRACTIONS.includes(parts.fraction)) {
          const option = this.node('option', '', 'Exact cm conversion'); option.value = parts.fraction; field.fraction.append(option); field.exactOption = option;
        }
        field.fraction.value = parts.fraction;
      }
      field.range.textContent = unit === 'in' ? `${field.minimum}–${field.maximum} inches` : `${formatCm(field.minimum * INCH_UNITS)}–${formatCm(field.maximum * INCH_UNITS)} cm`;
      field.displayUnit = unit; field.displayValue = field.input.value; field.displayFraction = field.fraction.value; field.displayEdited = false;
      field.converted.textContent = `Finished size: ${formatCm(field.units)} cm`;
    }
    finishMeasurementInput(name) {
      if (this.measurementUnit !== 'cm' || this.adding) return;
      const field = this.measurementFields[name];
      try { this.readMeasurementField(field); this.renderMeasurementField(field); }
      catch (_) { /* Keep an incomplete or invalid input visible for correction. */ }
    }
    setMeasurementUnit(unit) {
      if (this.adding || unit === this.measurementUnit || !['in', 'cm'].includes(unit)) return;
      try { this.readMeasurementFields(); } catch (error) { this.setMessage(error.message, true); return; }
      this.measurementUnit = unit;
      Object.values(this.measurementFields).forEach((field) => this.renderMeasurementField(field));
      this.unitButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.unit === unit)));
      if (this.confirm) this.confirm.checked = false;
      this.update();
    }
    readState() {
      const data = new FormData(this.form);
      const dimensions = this.measurementFields ? this.readMeasurementFields() : { width: Number(data.get('width')), length: Number(data.get('length')), measurementUnit: 'in' };
      return { color: Number(data.get('color')), header: Number(data.get('header')), lining: Number(data.get('lining')), ...dimensions, quantity: Number(data.get('quantity')), room: String(data.get('room') || '').trim() };
    }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' }).format(cents / 100); }
    setMessage(message, error = false) { this.message.textContent = message; this.message.classList.toggle('is-error', error); }
    async resolve(cents, fresh = false) {
      const chunk = this.priceIndex.chunks.find((entry) => cents >= entry.minimumCents && cents <= entry.maximumCents);
      if (!chunk) throw new Error('This configuration is currently unavailable. Please try a different size or lining.');
      const root = this.dataset.rootUrl || '/';
      const url = `${root.endsWith('/') ? root : `${root}/`}products/${chunk.handle}.js`;
      if (fresh || !this.products.has(chunk.handle)) this.products.set(chunk.handle, this.json(url, fresh));
      let product;
      try { product = await this.products.get(chunk.handle); } catch (error) { this.products.delete(chunk.handle); throw error; }
      if (product.variants.length !== chunk.count) throw new Error('The current pricing catalog is being updated. Please try again shortly.');
      const variant = product.variants.find((entry) => entry.option1 === priceLabel(cents));
      if (!variant || !variant.available || variant.price !== cents) throw new Error('The current price for this configuration could not be confirmed. Please try again.');
      return variant;
    }
    summary(quote) {
      this.querySelector('[data-unit-price]').textContent = this.money(quote.unitCents);
      this.querySelector('[data-panel-total]').textContent = this.money(quote.totalCents);
      this.querySelector('[data-total-price]').textContent = this.money(quote.totalCents);
      this.querySelector('[data-panel-count]').textContent = quote.quantity;
      const entries = [['Fabric', quote.color.label], ['Heading', quote.header.label], ['Size', `${sizeLabel(quote)} per panel${quote.measurementUnit === 'in' && !hasExactConversion(quote) ? ` (≈ ${displayCm(quote.widthUnits)} × ${displayCm(quote.lengthUnits)} cm)` : ''}`], ['Lining', quote.lining.label], ['Panels', String(quote.quantity)]];
      const list = this.querySelector('[data-summary-list]'); list.replaceChildren();
      entries.forEach(([key, value]) => list.append(this.node('dt', '', key), this.node('dd', '', value)));
      [['color', quote.color.label], ['header', quote.header.label], ['size', sizeLabel(quote)], ['lining', ''], ['quantity', String(quote.quantity)]].forEach(([key, value]) => { this.querySelector(`[data-selected="${key}"]`).textContent = value; });
    }
    hardwareSummary(bundle, quote) {
      const list = this.querySelector('[data-hardware-summary]'); list.replaceChildren();
      list.hidden = !bundle.selected;
      if (bundle.selected) bundle.summary.forEach(([key, value]) => list.append(this.node('dt', '', key), this.node('dd', '', value)));
      this.querySelector('[data-hardware-total-row]').hidden = !bundle.selected;
      this.querySelector('[data-hardware-total]').textContent = this.money(bundle.totalCents);
      this.querySelector('[data-total-price]').textContent = this.money(quote.totalCents + bundle.totalCents);
      this.button.textContent = bundle.selected ? 'Add drapery & hardware to bag →' : 'Add drapery to bag →';
    }
    async update() {
      // Media follows the checked color even while measurements are incomplete.
      this.syncColorGallery();
      const version = ++this.version;
      this.button.disabled = true;
      this.confirmedQuote = null;
      let quote;
      try {
        this.design?.assertReady();
        const state = this.readState();
        quote = calculate(this.config, state);
        this.design?.saveDraft(state);
        this.summary(quote);
        const context = { heading: quote.header.label, width: quote.width, length: quote.length, quantity: quote.quantity, room: this.readState().room };
        this.hardware.setContext(context);
        this.setMessage('Confirming your configured price…');
        const [variant, hardware] = await Promise.all([this.resolve(quote.unitCents), this.hardware.quote()]);
        if (version !== this.version || this.adding) return;
        this.hardwareSummary(hardware, quote);
        if (this.design?.mode === 'edit') this.button.textContent = 'Update this window in bag →';
        else if (this.design?.mode === 'copy') this.button.textContent = 'Add copied window to bag →';
        this.confirmedQuote = { ...quote, variant, hardware };
        this.button.disabled = !this.confirm.checked;
        this.setMessage(this.confirm.checked ? 'Your current prices are confirmed. Ready to add to your bag.' : 'Review your setup and confirm the measurements and quantities below.');
      } catch (error) {
        if (version === this.version) {
          if (!quote) { this.querySelector('[data-unit-price]').textContent = '—'; this.querySelector('[data-panel-total]').textContent = '—'; }
          this.querySelector('[data-total-price]').textContent = '—';
          this.querySelector('[data-hardware-summary]').hidden = true;
          this.querySelector('[data-hardware-total-row]').hidden = true;
          this.setMessage(error.message, true);
        }
      }
    }
    async add() {
      if (this.adding || !this.config || !this.confirmedQuote || !this.confirm.checked || this.button.disabled) return;
      this.adding = true;
      this.button.disabled = true;
      const fields = [...this.form.querySelectorAll('input,select,textarea,button')];
      const disabled = fields.map(field => field.disabled);
      let submitted = false;
      try {
        const state = this.readState();
        const quote = calculate(this.config, state);
        await this.design?.preparePurchase();
        fields.forEach((field) => { field.disabled = true; });
        this.setMessage('Adding your drapery…');
        const variant = await this.resolve(quote.unitCents, true);
        const group = `drapery-${Date.now()}-${crypto.getRandomValues(new Uint32Array(1))[0]}`;
        const hardware = await this.hardware.quote({fresh: true, bundleId: group, room: state.room});
        const image = new URL(this.dataset.image, window.location.origin).href;
        const productUrl = new URL(this.dataset.productUrl, window.location.origin).href;
        const properties = { 'Product title': this.dataset.title, 'Fabric color': quote.color.label, 'Heading': quote.header.label, ...productionProperties(quote), 'Lining': quote.lining.label, 'Order unit': 'One individual panel', 'Room / window': state.room, 'Measurements reviewed': 'Yes', '_Kalia pairing': hardware.selected ? group : '', '_Kalia group': group, '_Kalia role': 'product', '_Kalia charge required': 'no', '_Kalia parent variant': String(variant.id), '_Kalia configured title': this.dataset.title, '_Kalia configured image': image, '_Kalia configured url': productUrl, '_Kalia complete price': 'yes' };
        const items = [{ id: variant.id, quantity: quote.quantity, properties }, ...hardware.items];
        Object.assign(properties, this.design?.properties(state) || {});
        this.design?.beginPurchase(group);
        submitted = true;
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Please review your bag before trying this setup again.');
        const added = result.items?.find((item) => Number(item.variant_id || item.id) === Number(variant.id) && item.properties?.['_Kalia group'] === group);
        if (!added || added.quantity !== quote.quantity || added.price !== quote.unitCents) {
          this.setMessage('Your bag has been updated. Please review every product, price and quantity in your bag.');
        }
        await this.design?.finishPurchase({ added, group, variant, quantity: quote.quantity, properties });
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (submitted) {
          this.setMessage('Opening your bag to confirm which items were added. Review the contents before retrying.');
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        this.adding = false;
        fields.forEach((field, index) => { field.disabled = disabled[index]; });
        this.confirm.checked = false;
        this.confirmedQuote = null;
        this.setMessage(error.message, true);
        this.button.disabled = true;
      }
    }
  }
  if (!customElements.get('kalia-drapery')) customElements.define('kalia-drapery', KaliaDrapery);
})();
